import dayjs from 'dayjs'
import { useQuery } from '@tanstack/react-query'
import { Badge, EmptyState, Image, Select, Text } from '@cinesend/ui-kit'
import DataTable from '../data_table'
import RowMenu from '../row_menu'
import { Trash } from '@phosphor-icons/react'
import useStore from '../../hooks/use_store'
import { useParams } from 'react-router-dom'
import { humanFileSize } from '../../helpers/human_file_size'
import useTableFilters from '../../hooks/use_table_filters'
import ImageModal from '../../pages/images/image_modal'
import { useState } from 'react'

const sortingOptions = [
  { label: 'File name', key: 'file_name' },
  { label: 'File size', key: 'size' },
  { label: 'Created', key: 'created_at' },
]

const filteringOptions = [
  {
    label: 'Status',
    key: 'status',
    type: 'checkboxes',
    options: [
      { label: 'Unmatched', key: 'unmatched', value: 'unmatched' },
      { label: 'Matched', key: 'matched', value: 'matched' },
    ],
  },
]

const ImagesTable = ({ type, onClick }) => {
  const cycleID = useParams().cycleID
  const showFilter = !type
  const { queryString, searching, sorting, filtering, pagination } = useTableFilters({
    sortingOptions,
    filteringOptions: showFilter ? filteringOptions : null,
    enableSearch: true,
    initialFilter: showFilter ? { status: ['unmatched'] } : {},
    persistKey: showFilter ? `images-table-${cycleID}-status` : `images-table-${cycleID}`,
  })
  const selectedStatus = filtering?.filters?.status ?? []
  const imageType = type ?? (selectedStatus.length === 1 ? selectedStatus[0] : 'all')
  const [showImage, setShowImage] = useState(null)
  const { deleteModel } = useStore()
  const airlineID = useParams().airlineID
  const {
    data: imagesData,
    error,
    isLoading,
    refetch,
  } = useQuery([`/asset-images`, `${queryString}&type=${imageType}&airlineID=${airlineID}&cycleID=${cycleID}`])
  const columns = [
    {
      key: 'image',
      header: 'Image',
      render: (data) => (
        <div className='flex max-w-24 max-h-24 items-center'>
          <Image src={data.url} alt={data.file_name} ratio='video' />
        </div>
      ),
    },
    {
      key: 'file_name',
      header: 'File name',
      render: (data) => (
        <Text variant='sm-medium' tone='primary'>
          {data.file_name}
        </Text>
      ),
    },
    { key: 'asset', header: 'Asset', render: (data) => data.asset?.title },
    { key: 'size', header: 'File size', render: (data) => humanFileSize(data.size) },
    {
      key: 'status',
      header: 'Status',
      render: (data) => (
        <Badge tone={data.asset_id && data.field_id ? 'success' : 'warning'}>
          {data.asset_id && data.field_id ? 'MATCHED' : 'UNMATCHED'}
        </Badge>
      ),
    },
    {
      key: 'created_at',
      header: 'Created',
      render: (data) => (data.created_at ? dayjs(data.created_at).format('MMM D, YYYY, h:mm A') : 'N/A'),
    },
    {
      key: 'actions',
      header: '',
      width: '65px',
      render: (data) => (
        <RowMenu
          label={`Actions for ${data.file_name}`}
          items={[
            {
              id: 'delete',
              label: 'Delete',
              tone: 'danger',
              icon: <Trash size={20} />,
              onSelect: () =>
                deleteModel.mutate({
                  endpoint: '/asset-images',
                  id: data.id,
                  params: `?cycleID=${cycleID}&assetID=${data.asset_id ?? ''}`,
                  deleteNoun: 'image',
                  deleteName: data.file_name,
                }),
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      <DataTable
        caption='Images'
        columns={columns}
        rows={imagesData?.data?.data ?? []}
        onRowClick={(data) => (typeof onClick === 'function' ? onClick(data) : setShowImage(data))}
        isLoading={isLoading}
        error={error}
        onRetry={refetch}
        empty={<EmptyState title='No images found!' detail='Upload new images to begin.' />}
        searching={searching}
        sorting={sorting}
        filtering={filtering}
        filterControl={
          showFilter ? (
            <Select
              label='Status'
              hideLabel
              modal={false}
              value={filtering.filters.status?.[0] ?? 'all'}
              onValueChange={(value) => {
                const next = { ...filtering.filters }
                if (!value || value === 'all') delete next.status
                else next.status = [value]
                filtering.onFiltersChange(next)
              }}
              options={[
                { value: 'all', label: 'All statuses' },
                { value: 'unmatched', label: 'Unmatched' },
                { value: 'matched', label: 'Matched' },
              ]}
            />
          ) : null
        }
        pagination={pagination}
        totalRows={imagesData?.data?.total}
      />
      {showImage && <ImageModal image={showImage} onClose={() => setShowImage(null)} />}
    </>
  )
}

export default ImagesTable
